/**
 * Migra grafos vindos de caches/mocks antigos pro shape atual.
 * Idempotente — rodar várias vezes no mesmo grafo não muda nada.
 *
 * Hoje cobre:
 *  - `update-field` (singular, com `{ fieldPath, value }`) → `update-fields`
 *    (plural, com `{ updates: [{ id, fieldPath, value }] }`).
 *  - `notify` com `recipient: 'driver' | 'internal'` → `'customer'`. Por
 *    enquanto só notificamos o cliente; valores legados são normalizados.
 *
 * Sempre que renomear NodeKind ou mudar shape de config, adicionar a regra
 * aqui e chamar do loadGraph.
 */

import { nanoid } from 'nanoid';
import type { WorkflowGraph, WorkflowNode } from '../types';

interface LegacyUpdateFieldNode {
  kind: 'update-field';
  config: { fieldPath: string; value: string };
}

function migrateNode(node: WorkflowNode | LegacyUpdateFieldNode): WorkflowNode {
  if ((node as { kind: string }).kind === 'update-field') {
    const legacy = node as unknown as WorkflowNode & {
      config: { fieldPath: string; value: string };
    };
    return {
      ...legacy,
      kind: 'update-fields',
      config: {
        updates: [
          {
            id: nanoid(6),
            fieldPath: legacy.config.fieldPath ?? '',
            value: legacy.config.value ?? '',
          },
        ],
      },
    } as unknown as WorkflowNode;
  }
  if ((node as { kind: string }).kind === 'notify') {
    const n = node as WorkflowNode & {
      config: { recipient?: string };
    };
    if (n.config.recipient !== 'customer') {
      return {
        ...n,
        config: { ...n.config, recipient: 'customer' },
      } as unknown as WorkflowNode;
    }
  }
  return node as WorkflowNode;
}

export function migrateGraph(graph: WorkflowGraph): WorkflowGraph {
  let touched = false;
  const nodes = graph.nodes.map((n) => {
    const next = migrateNode(n);
    if (next !== n) touched = true;
    return next;
  });
  if (!touched) return graph;
  return { ...graph, nodes };
}
